import { integrationApi } from "@repo/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { getCronExpression } from "../../../utils/schedule";
import { getRegionsForApi } from "./utils";

interface UseCreateIntegrationProps {
  onSuccess: (
    message: string,
    isDraft?: boolean,
    integrationType?: string,
  ) => void;
  onError: (message: string) => void;
}

export function useCreateIntegration({
  onSuccess,
  onError,
}: UseCreateIntegrationProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      integrationType,
      isEnabled,
      data,
    }: {
      integrationType: string;
      isEnabled: boolean;
      data: Record<string, unknown>;
    }) => {
      // featureNames is always an array ([] before API loads, populated after), so || fallback
      // won't trigger for an empty array — submit is disabled until the form is valid, by which
      // time the API response is available and featureNames will be populated.
      const featureNames = (data.featureNames as string[]) || [
        "POSTURE_FOR_AWS",
      ];
      const features = featureNames.map((featureName) => ({
        featureName,
        featureConfig: {
          regions: getRegionsForApi((data.regions as string[]) || ["All"]),
        },
        schedules: [
          {
            scheduleName: "Daily",
            cronExpression: getCronExpression(
              (data.frequency as string) || "24hours",
              data.importTime as Date | null,
            ),
          },
        ],
      }));

      const payload = {
        displayName: (data.name as string) || "",
        isEnabled,
        credentials: {
          type: "AWS_ROLE",
          data: {
            roleArn: (data.role_arn as string) || "",
            externalId: (data.external_id as string) || "",
          },
          metadata: {},
        },
        features,
        metadata: {
          accountId: (data.account_id as string) || "",
          description: data.description as string,
        },
      };
      return integrationApi.createIntegration(integrationType, payload);
    },
    onMutate: async ({ integrationType, isEnabled, data }) => {
      await queryClient.cancelQueries({ queryKey: ["integrations"] });
      const previousIntegrations = queryClient.getQueryData(["integrations"]);

      queryClient.setQueryData(["integrations"], (old: unknown) => {
        const oldData = old as
          | { integrations?: Array<Record<string, unknown>> }
          | undefined;
        const newIntegration = {
          id: Date.now(),
          displayName: (data.name as string) || "",
          description: data.description as string,
          integrationType,
          connectionStatus: "PENDING",
          isEnabled,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return {
          ...oldData,
          integrations: [newIntegration, ...(oldData?.integrations || [])],
        };
      });

      return { previousIntegrations };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousIntegrations) {
        queryClient.setQueryData(
          ["integrations"],
          context.previousIntegrations,
        );
      }
      onError(
        intl.formatMessage({
          id: "l8gxpc",
          defaultMessage: "Failed to create integration",
          description: "Error message for integration creation failure",
        }),
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      const isDraft = !variables.isEnabled;
      if (isDraft) {
        onSuccess(
          intl.formatMessage(
            {
              id: "c2bz4O",
              defaultMessage:
                "You can resume the setup of {integrationType} at any time.",
              description: "Draft save success message",
            },
            {
              integrationType: variables.data.name as string,
            },
          ),
          true,
          variables.data.name as string,
        );
      } else {
        onSuccess(
          intl.formatMessage({
            id: "gRl5vB",
            defaultMessage: "Integration created successfully",
            description: "Success message for integration creation",
          }),
          false,
        );
      }
    },
  });
}
